import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { deepPurple } from "@mui/material/colors";
import { Alert, Chip, Button as MuiButton } from "@mui/material";
import { Typography, Stack, Divider, Grid, Box, Button, Link } from "@financeable-com-au/financeable-ui";
import { startCase } from "lodash";
import { AppDispatch } from "@/store/store";
import FormInputField from "@/components/CommonComponents/FormInputField";
import FormAutoCompleteField from "@/components/CommonComponents/FormAutoCompleteField";
import {
  getSampleDocument,
  getTemplatesById,
  previewDocument,
  saveDocumentTemplate,
  templatesSelector,
} from "@/store/slices/templatesSlice";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import {
  ALLOWED_DOCUMENT_TEMPLATE_FILE_TYPES,
  documentTemplatePath,
  ERRORS_MESSAGE,
  MAX_DOCUMENT_UPLOAD_SIZE,
  SECTION_TYPE,
  SUCCESS_MESSAGE,
} from "@/constants";
import { formatDateTime } from "@/utils/formatDate";
import FileUploadDropzone from "@/components/FileUpload/FileUploadDropzone";
// @ts-expect-error: ignoring as it is existing js file
import { viewFile } from "@/components/Utils/helperFunction";
import { IDispatchResponse } from "@/utils/interface";
import colors from "@/style/colors";

export interface DocumentTemplateProps {
  templateId: string;
}

export interface saveTemplateData {
  templateId: string;
  name: string;
  subject: string;
  messageType: string;
  templateType: string;
  variables: string[];
  body?: string;
}

const CustomInfoTextWrapper = ({ children, style }: { children: React.ReactNode, style?: React.CSSProperties }) => {
  return (
    <Box
      sx={{
        borderLeft: `4px solid ${colors.skyBlue}`,
        bgcolor: colors.whiteSkyBlue,
        p: 2,
        borderRadius: 2,
        mb: 5,
        ...style,
      }}
    >
      {children}
    </Box>
  )
}

const HightLightedInfoTextWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      sx={{
        bgcolor: colors.lightSkyBlue,
        border: `1px solid ${colors.babyBlue}`,
        p: 1,
        borderRadius: 5,
        fontFamily: "monospace",
        fontSize: "0.8rem",
        display: "inline-block",
        my: 1,
      }}
    >
      {children}
    </Box>
  )
}

const CalculatedFields = ({ varKey, variables, handleCopyTag }: { varKey: string, variables: string[], handleCopyTag: (tag: string) => void }) => {
  const [formState, setFormState] = useState({
    selectedVariable: "",
    calculationMethod: "",
    valueType: "",
    inputValue: "",
  });

  const numberTypeCalculationMethods = ["Divide", "Multiply"];
  const percentageOrDollarTypeCalculationMethods = ["Increase", "Decrease"];
  const calculationMethods = [...percentageOrDollarTypeCalculationMethods, ...numberTypeCalculationMethods];
  const valueTypes = ["Percentage", "Dollar", "Number"];

  const handleFieldChange = (value: string, fieldName: string) => {
    if (fieldName === "inputValue") {
      // Only allow numbers
      if (value === "" || /^\d*\.?\d*$/.test(value)) {
        setFormState((prev) => ({
          ...prev,
          [fieldName]: value,
        }));
      }
      return
    } else if (fieldName === "calculationMethod") {
      if (numberTypeCalculationMethods.includes(value)) {
        setFormState((prev) => ({
          ...prev,
          [fieldName]: value,
          valueType: "Number",
        }));
        return
      } else if (percentageOrDollarTypeCalculationMethods.includes(value)) {
        setFormState((prev) => ({
          ...prev,
          [fieldName]: value,
          valueType: "Percentage",
        }));
        return
      }
    }
    setFormState((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleBlur = (fieldName: string, value: string) => {
    // Handle blur if needed
  };

  const generateTagValue = () => {
    const { selectedVariable, calculationMethod, inputValue, valueType } = formState;
    if (selectedVariable && calculationMethod && inputValue && valueType) {
      return `<<${varKey}.${selectedVariable}_${calculationMethod.toLowerCase()}_${inputValue}_${valueType.toLowerCase()}>>`;
    }
    return "";
  };

  const tagValue = generateTagValue();

  const isDisabledValueType = numberTypeCalculationMethods.includes(formState?.calculationMethod) ? true : false;
  const isDisabledNumberValueType = percentageOrDollarTypeCalculationMethods.includes(formState?.calculationMethod) ? true : false;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography level="body-md" style={{ margin: "1rem 0" }} bold>
        {startCase(varKey)} Calculated Fields
      </Typography>
      <Grid container gap={2}>
        <FormAutoCompleteField
          fieldName="selectedVariable"
          label="Variables"
          value={formState.selectedVariable}
          options={variables}
          onChange={handleFieldChange}
          onBlur={handleBlur}
          getOptionLabel={(option) => option || ""}
          gridProps={{ md: 3, sm: 12, xs: 12 }}
          variant="outlined"
          size="small"
        />

        <FormAutoCompleteField
          fieldName="calculationMethod"
          label="Calculation Methods"
          value={formState.calculationMethod}
          options={calculationMethods}
          onChange={handleFieldChange}
          onBlur={handleBlur}
          getOptionLabel={(option) => option || ""}
          gridProps={{ md: 3, sm: 12, xs: 12 }}
          variant="outlined"
          size="small"
        />

        <FormAutoCompleteField
          fieldName="valueType"
          label="Value Type"
          value={formState.valueType}
          options={isDisabledNumberValueType ? valueTypes.filter((type) => type !== "Number") : valueTypes}
          onChange={handleFieldChange}
          onBlur={handleBlur}
          disabled={isDisabledValueType}
          getOptionLabel={(option) => option || ""}
          gridProps={{ md: 3, sm: 12, xs: 12 }}
          variant="outlined"
          size="small"
        />

        <FormInputField
          fieldName="inputValue"
          label="Input Value"
          value={formState.inputValue}
          onChange={handleFieldChange}
          onBlur={handleBlur}
          inputProps={{
            type: "number",
            step: "any",
            min: "0"
          }}
          gridProps={{ md: 2, sm: 12, xs: 12 }}
        />
      </Grid>

      {tagValue && (
        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: "12px" }}>
          <Typography level="body-md" style={{ margin: "1rem 0" }} bold>
            Final Output:
          </Typography>
          <MuiButton
            variant="contained"
            disableElevation={true}
            style={{
              textTransform: "none",
            }}
            onClick={() => handleCopyTag(tagValue)}
          >
            {tagValue}
          </MuiButton>
        </Box>
      )}
    </Box>
  )
}

const CreateNumberVariables = ({ handleCopyTag }: { handleCopyTag: (tag: string) => void }) => {
  const [formState, setFormState] = useState({
    inputValue: 1,
  });

  const handleFieldChange = (value: string, fieldName: string) => {
    setFormState((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const generateTagValue = () => {
    const { inputValue } = formState;
    if (inputValue) {
      return `<<number.${inputValue}>>`;
    }
    return "";
  };

  const tagValue = generateTagValue();

  return (
    <Box sx={{ mb: 3, mt: 5 }}>
      <Typography level="body-md" style={{ margin: "1rem 0" }} bold>
        Create Number Variables
      </Typography>
      <Grid container gap={4}>
        <FormInputField
          fieldName="inputValue"
          label="Input Value"
          value={formState.inputValue}
          onChange={handleFieldChange}
          onBlur={() => { }}
          inputProps={{
            type: "number",
            step: "any",
            min: "0"
          }}
          gridProps={{ md: 2, sm: 12, xs: 12 }}
        />
        <Grid md={6} sm={12} xs={12}>
          {tagValue && (
            <Box sx={{ display: "flex", alignItems: "start", gap: "12px" }}>
              <Typography level="body-md" style={{ margin: "0.5rem 0" }} bold>
                Final Output:
              </Typography>
              <MuiButton
                variant="contained"
                disableElevation={true}
                style={{
                  textTransform: "none",
                }}
                onClick={() => handleCopyTag(tagValue)}
              >
                {tagValue}
              </MuiButton>
            </Box>
          )}
        </Grid>
      </Grid>


    </Box>

  )
}

const DocumentTemplateUpload = ({ templateId }: DocumentTemplateProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const {
    currentlyEditingTemplate,
    isFetchingDocument,
    allowedVariables,
    allowCalculatedFields,
    isFetchingPreviewDocument,
    isFetchingSampleDocument,
  } = useSelector(templatesSelector);
  const { name, subject, messageType, templateType, variables } = currentlyEditingTemplate;
  const operators = ['plus', 'minus', 'multiply', 'division'];
  const calculationWrapperVariables = ['calc', '/calc'];

  const handleOnBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1); // Only go back if there's a history to go back to
    } else {
      navigate(documentTemplatePath); // Navigate to a fallback route
    }
  };

  const saveTemplateHandler = useCallback(
    (files: File[]) => {
      const newSelectedFiles = [];
      const formData = new FormData();

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (ALLOWED_DOCUMENT_TEMPLATE_FILE_TYPES.includes(file.type)) {
          if (file.size <= MAX_DOCUMENT_UPLOAD_SIZE) {
            newSelectedFiles.push(file);
          }
        }
      }
      if (newSelectedFiles?.length) {
        newSelectedFiles.forEach((file) => {
          formData.append("file", file);
        });
        formData.append("messageType", messageType);
        formData.append("templateType", templateType);
        formData.append("name", name);
        formData.append("subject", subject);
        formData.append("variables", variables as unknown as Blob);

        dispatch(saveDocumentTemplate({ formData })).then((res: IDispatchResponse) => {
          if (!res?.error) {
            dispatch(
              openSnackbar({
                message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.fileUploadSuccess,
                intent: "success",
              }),
            );
          } else {
            dispatch(
              openSnackbar({
                message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
                intent: "danger",
              }),
            );
          }
        });
      }
    },
    [currentlyEditingTemplate],
  );

  useEffect(() => {
    dispatch(getTemplatesById({ templateId }));
  }, [templateId]);

  const handle = {
    copyTag: (tag: string) => {
      navigator.clipboard.writeText(tag);
      dispatch(
        openSnackbar({
          message: "Tag copied to clipboard",
          intent: "success",
        }),
      );
    },
    previewDocument: () => {
      if (!currentlyEditingTemplate?.body) {
        dispatch(
          openSnackbar({
            message: "Please upload a document to preview",
            intent: "danger",
          }),
        );
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch(previewDocument({ templateId })).then((res: any) => {
        if (res.error) {
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        } else {
          dispatch(
            openSnackbar({
              message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.fetchPreviewDocumentSuccess,
              intent: "success",
            }),
          );
          // Open the preview document in a new tab.
          viewFile(res?.payload?.data?.data);
        }
      });
    },
    previewSampleDocument: (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      dispatch(getSampleDocument({ templateType })).then(({ error, payload }: IDispatchResponse) => {
        if (error) {
          dispatch(
            openSnackbar({
              message: payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        } else {
          dispatch(
            openSnackbar({
              message: payload?.data?.status_message || SUCCESS_MESSAGE.fetchPreviewDocumentSuccess,
              intent: "success",
            }),
          );
          // Open the preview document in a new tab.
          viewFile(payload?.data?.data);
        }
      });
    },
  };

  return (
    <>
      <Button
        intent="neutral"
        variant="inline"
        onClick={handleOnBack}
        startDecorator={<ChevronLeftIcon htmlColor={deepPurple[500]} sx={{ fontSize: "1rem" }} />}
      >
        Back to list
      </Button>
      <Stack
        direction="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        padding="24px 0"
        width="100%"
        rowGap={2}
      >
        <Typography component="h1" level="title-lg">
          {currentlyEditingTemplate?.name}
        </Typography>
        <Stack direction="row" justifyContent="flex-start" alignItems="center" columnGap={1} mb={2}>
          <Typography level="body-sm">Version</Typography>
          {/* TODO: Replace Chip component with Financeable UI (https://financeable.atlassian.net/browse/FWA-389) */}
          <Chip
            label={currentlyEditingTemplate?.updatedAt ? formatDateTime(currentlyEditingTemplate.updatedAt) : "N/A"}
            size="small"
            sx={{
              backgroundColor: "rgba(0, 0, 0, 0.05)",
              fontWeight: "500",
              fontSize: "12px",
            }}
          />
        </Stack>
        <Divider />
      </Stack>
      {currentlyEditingTemplate?._id && (
        <Grid container xs={12} spacing={4} sx={{ padding: 4 }}>
          <Grid xs={12} md={5}>
            <FileUploadDropzone
              isLoading={isFetchingDocument}
              isMultiple={false}
              saveFilesHandler={saveTemplateHandler}
              fileData={currentlyEditingTemplate}
              previewDocumentHandler={handle.previewDocument}
              isFetchingPreviewDocument={isFetchingPreviewDocument}
              sectionType={SECTION_TYPE.DOCUMENT_TEMPLATE}
            />
          </Grid>
          <Grid xs={12} md={7} sx={{ height: "100%", overflowY: "auto", maxHeight: "calc(100vh - 300px)" }}>
            <>
              <CustomInfoTextWrapper>
                <Typography level="body-sm" gutterBottom>
                  To include dynamic data in your documents, please add the tags below into your document. The tag
                  should be placed where you would like the data to appear. For example, the customer’s name:
                </Typography>

                {/* Highlighted section */}
                <HightLightedInfoTextWrapper>
                  Name: &lt;&lt;customer.firstName&gt;&gt; &lt;&lt;customer.lastName&gt;&gt;
                </HightLightedInfoTextWrapper>

                <Typography level="body-sm" style={{ marginTop: "0.5rem" }}>
                  Check out this{" "}
                  <Link
                    href=""
                    underline="hover"
                    sx={{ cursor: "pointer" }}
                    disabled={isFetchingSampleDocument}
                    onClick={handle.previewSampleDocument}
                  >
                    {isFetchingSampleDocument ? "Loading..." : "sample"}
                  </Link>{" "}
                  to see how to best use the tags in your documents. If you have any questions, start a support chat by
                  clicking the button on the bottom left-hand corner of the page. Checkout{" "}
                  <Link
                    href=""
                    underline="hover"
                    sx={{ cursor: "pointer" }}
                    disabled={isFetchingSampleDocument}
                    onClick={handle.previewSampleDocument}
                  >
                    {isFetchingSampleDocument ? "Loading..." : "sample document"}
                  </Link>
                  .
                </Typography>
              </CustomInfoTextWrapper>

              <Alert
                severity="info"
                sx={{
                  padding: "2px 8px",
                  fontSize: "0.8rem",
                  minHeight: "unset",
                  lineHeight: "1.2",
                  "& .MuiAlert-icon": {
                    fontSize: "1.2rem",
                  },
                }}
              >
                <Typography level="body-sm">Note: click on the tag to copy it to the clipboard.</Typography>
              </Alert>
              {allowedVariables &&
                Object.entries(allowedVariables as Record<string, string[]>).map(([varKey, children]) => {
                  // Sort children according to the defined order
                  const orderedChildren = [...(children as string[])].sort();
                  return (
                    <>
                      <Typography level="body-lg" style={{ margin: "1rem 0" }}>
                        {varKey
                          .split(/(?=[A-Z])/)
                          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                          .join(" ")
                          .replace(/\bUrl\b/g, "URL")}{" "}
                        {varKey === "user" ? "(Broker)" : ""}
                        {varKey === "productsQuote" ? "(Saved lender quote)" : ""}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "12px",
                          mb: 2,
                        }}
                      >
                        {varKey === "productsQuote" ? (
                          <Grid style={{ paddingTop: "0" }}>
                            <ProductQuoteVariables
                              orderedChildren={orderedChildren}
                              varKey={varKey}
                              handle={handle}
                              quoteNumber="one"
                            />
                            <ProductQuoteVariables
                              orderedChildren={orderedChildren}
                              varKey={varKey}
                              handle={handle}
                              quoteNumber="two"
                            />
                            <ProductQuoteVariables
                              orderedChildren={orderedChildren}
                              varKey={varKey}
                              handle={handle}
                              quoteNumber="three"
                            />
                          </Grid>
                        ) : (
                          <MappedVariables orderedChildren={orderedChildren} varKey={varKey} handle={handle} />
                        )}
                      </Box>
                      {allowCalculatedFields?.[varKey] && (
                        <CalculatedFields
                          varKey={varKey}
                          variables={allowCalculatedFields[varKey] || []}
                          handleCopyTag={handle.copyTag}
                        />
                      )}
                    </>
                  );
                })}

              {/* Calculated Fields Section: */}
              <CustomInfoTextWrapper style={{ marginTop: "2rem" }}>
                <Typography level="body-sm" gutterBottom>
                  To include calculated fields in your documents, please add the tags below into your document.
                  Your all custom calculated fields will be only placed between &lt;&lt;calc&gt;&gt; &lt;&lt;/calc&gt;&gt; tags.
                </Typography>

                {/* Highlighted section */}
                <HightLightedInfoTextWrapper>
                  Calculation: &lt;&lt;calc&gt;&gt; &lt;&lt;variable&gt;&gt; &lt;&lt;operator&gt;&gt;  &lt;&lt;variable&gt;&gt;  &lt;&lt;/calc&gt;&gt;
                </HightLightedInfoTextWrapper>

                <HightLightedInfoTextWrapper>
                  Ex: &lt;&lt;calc&gt;&gt; &lt;&lt;asset.assetValue&gt;&gt; &lt;&lt;minus&gt;&gt;  &lt;&lt;asset.deposit&gt;&gt;  &lt;&lt;/calc&gt;&gt;
                </HightLightedInfoTextWrapper>
              </CustomInfoTextWrapper>

              <Typography level="body-lg" style={{ margin: "1rem 0" }}>
                Calculation Wrapper variables
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "12px",
                  mb: 2,
                }}
              >
                <MappedVariables orderedChildren={calculationWrapperVariables} handle={handle} varSection="calculations" />
              </Box>

              <Typography level="body-lg" style={{ margin: "1rem 0" }}>
                Operators
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "12px",
                  mb: 2,
                }}
              >
                <MappedVariables orderedChildren={operators} handle={handle} varSection="operators" />
              </Box>
              <CreateNumberVariables handleCopyTag={handle.copyTag} />
            </>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default DocumentTemplateUpload;

const ProductQuoteVariables = ({
  orderedChildren,
  varKey,
  handle,
  quoteNumber,
}: {
  orderedChildren: string[];
  varKey: string;
  handle: { copyTag: (tag: string) => void };
  quoteNumber: string;
}) => {
  return (
    <Grid container spacing={2}>
      <Grid xs={12}>
        <Typography level="body-md" style={{ margin: "1.5rem 0" }}>
          Quote {startCase(quoteNumber)}
        </Typography>
      </Grid>
      <Grid container xs={12} spacing={2} style={{ gap: "12px" }}>
        <MappedVariables
          orderedChildren={orderedChildren.filter((child: string) => child.includes(`${quoteNumber}.`))}
          varKey={varKey}
          handle={handle}
        />
      </Grid>
    </Grid>
  );
};

const MappedVariables = ({
  orderedChildren,
  varKey,
  varSection,
  handle,
}: {
  orderedChildren: string[];
  varKey?: string;
  varSection?: string;
  handle: { copyTag: (tag: string) => void };
}) => {
  return orderedChildren.map((child: string, index: number) => {
    let tagValue = ''
    if (varKey) {
      tagValue = `<<${varKey}.${child}>>`;
    } else {
      tagValue = `<<${child}>>`;
    }

    return (
      <MuiButton
        className={`${varKey?.toLowerCase() || varSection?.toLowerCase() || 'operator'}-vars btn`}
        key={index}
        variant="contained"
        disableElevation={true}
        style={{
          textTransform: "none",
        }}
        onClick={() => handle.copyTag(tagValue)}
      >
        {tagValue}
      </MuiButton>
    );
  });
};
